// Per-user storage: routes all localStorage.* calls through the signed-in user namespace.
import { scopedStorage as localStorage } from "../services/userStorage";
import React, { useState, useEffect } from 'react';
import { BookOpen, Check, Clock, Repeat, Star, StarOff, Eye, EyeOff } from 'lucide-react';

const ScriptureMemorization = () => {
    const [memoryVerses, setMemoryVerses] = useState([]);
    const [newReference, setNewReference] = useState('');
    const [newText, setNewText] = useState('');
    const [practiceMode, setPracticeMode] = useState(false);
    const [activeVerse, setActiveVerse] = useState(null);
    const [hiddenWords, setHiddenWords] = useState([]);
    const [reviewDates, setReviewDates] = useState({});
    const [showAddForm, setShowAddForm] = useState(false);

    // Load saved memory verses from localStorage
    useEffect(() => {
        try {
            const savedVerses = localStorage.getItem('memory_verses');
            const savedReviews = localStorage.getItem('verse_review_dates');

            if (savedVerses) {
                setMemoryVerses(JSON.parse(savedVerses));
            }

            if (savedReviews) {
                setReviewDates(JSON.parse(savedReviews));
            }
        } catch (error) {
            console.error('Error loading memory verses:', error);
        }
    }, []);

    // Save verses to localStorage when they change
    useEffect(() => {
        localStorage.setItem('memory_verses', JSON.stringify(memoryVerses));
    }, [memoryVerses]);

    // Save review dates to localStorage when they change
    useEffect(() => {
        localStorage.setItem('verse_review_dates', JSON.stringify(reviewDates));
    }, [reviewDates]);

    // Calculate which verses need review based on spaced repetition
    const getVersesForReview = () => {
        const today = new Date();

        return memoryVerses.filter(verse => {
            if (!reviewDates[verse.id]) return true; // Never reviewed

            const lastReview = new Date(reviewDates[verse.id].lastReview);
            const reviewLevel = reviewDates[verse.id].level || 0;

            // Spaced repetition intervals (in days): 1, 3, 7, 14, 30, 90
            const intervals = [1, 3, 7, 14, 30, 90];
            const interval = intervals[Math.min(reviewLevel, intervals.length - 1)];

            // Add interval days to last review date
            const nextReviewDate = new Date(lastReview);
            nextReviewDate.setDate(nextReviewDate.getDate() + interval);

            return today >= nextReviewDate;
        });
    };

    // Add a new verse to memorize
    const handleAddVerse = (e) => {
        e.preventDefault();

        if (!newReference.trim() || !newText.trim()) return;

        const newVerse = {
            id: Date.now().toString(),
            reference: newReference.trim(),
            text: newText.trim(),
            isFavorite: false,
            dateAdded: new Date().toISOString()
        };

        setMemoryVerses([newVerse, ...memoryVerses]);
        setNewReference('');
        setNewText('');
        setShowAddForm(false);
    };

    // Remove a verse from the list
    const handleRemoveVerse = (id) => {
        setMemoryVerses(memoryVerses.filter(verse => verse.id !== id));

        // Also remove from review dates if exists
        if (reviewDates[id]) {
            const updatedReviewDates = { ...reviewDates };
            delete updatedReviewDates[id];
            setReviewDates(updatedReviewDates);
        }
    };

    // Toggle favorite status
    const toggleFavorite = (id) => {
        setMemoryVerses(
            memoryVerses.map(verse =>
                verse.id === id
                    ? { ...verse, isFavorite: !verse.isFavorite }
                    : verse
            )
        );
    };

    // Start practicing a verse
    const startPractice = (verse) => {
        setActiveVerse(verse);
        setPracticeMode(true);

        // Initially hide 20% of words randomly
        const words = verse.text.split(' ');
        const numToHide = Math.max(1, Math.floor(words.length * 0.2));

        const initialHidden = [];
        while (initialHidden.length < numToHide) {
            const randomIndex = Math.floor(Math.random() * words.length);
            if (!initialHidden.includes(randomIndex)) {
                initialHidden.push(randomIndex);
            }
        }

        setHiddenWords(initialHidden);
    };

    // Hide more words (increase difficulty)
    const increaseHiddenWords = () => {
        if (!activeVerse) return;

        const words = activeVerse.text.split(' ');
        const currentHiddenCount = hiddenWords.length;
        const maxHiddenCount = words.length;

        // Don't try to hide more than available
        if (currentHiddenCount >= maxHiddenCount) return;

        // Find words that aren't hidden yet
        const visibleWordIndices = [];
        for (let i = 0; i < words.length; i++) {
            if (!hiddenWords.includes(i)) {
                visibleWordIndices.push(i);
            }
        }

        // Randomly select some more words to hide (up to 25% more)
        const additionalToHide = Math.min(
            visibleWordIndices.length,
            Math.ceil(words.length * 0.25)
        );

        // Shuffle visible indices and take the additional count
        const shuffled = visibleWordIndices.sort(() => 0.5 - Math.random());
        const newHiddenIndices = shuffled.slice(0, additionalToHide);

        setHiddenWords([...hiddenWords, ...newHiddenIndices]);
    };

    // Mark a verse as reviewed (for spaced repetition)
    const markAsReviewed = (id) => {
        // Get current review data or initialize
        const currentReviewData = reviewDates[id] || { level: 0, lastReview: null };

        // Increase level unless it's already at max
        const newLevel = Math.min(5, currentReviewData.level + 1);

        setReviewDates({
            ...reviewDates,
            [id]: {
                level: newLevel,
                lastReview: new Date().toISOString()
            }
        });

        // Exit practice mode
        setPracticeMode(false);
        setActiveVerse(null);
    };

    // Reset practice session
    const resetPractice = () => {
        setPracticeMode(false);
        setActiveVerse(null);
        setHiddenWords([]);
    };

    // Render verse for practice with hidden words
    const renderPracticeVerse = () => {
        if (!activeVerse) return null;

        const words = activeVerse.text.split(' ');

        return (
            <div className="practice-verse">
                {words.map((word, index) => (
                    <span key={index} className="mx-1">
                        {hiddenWords.includes(index) ? (
                            <span className="inline-block w-12 border-b-2 border-indigo-300 text-center">
                                __
                            </span>
                        ) : (
                            <span>{word}</span>
                        )}
                    </span>
                ))}
            </div>
        );
    };

    // Get next scheduled review date
    const getNextReviewDate = (id) => {
        if (!reviewDates[id]) return 'Not reviewed yet';

        const lastReview = new Date(reviewDates[id].lastReview);
        const reviewLevel = reviewDates[id].level || 0;

        // Spaced repetition intervals (in days)
        const intervals = [1, 3, 7, 14, 30, 90];
        const interval = intervals[Math.min(reviewLevel, intervals.length - 1)];

        // Calculate next review date
        const nextReview = new Date(lastReview);
        nextReview.setDate(nextReview.getDate() + interval);

        // Format relative to today
        const today = new Date();
        const diffDays = Math.ceil((nextReview - today) / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return 'Review now';
        if (diffDays === 0) return 'Review today';
        if (diffDays === 1) return 'Review tomorrow';
        return `Review in ${diffDays} days`;
    };

    // Format date for display
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    return (
        <div className="scripture-memorization">
            {practiceMode && activeVerse ? (
                <div className="practice-container bg-indigo-50 p-6 rounded-lg mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-indigo-800">
                            Practicing: {activeVerse.reference}
                        </h3>
                        <button
                            onClick={resetPractice}
                            className="btn btn-sm btn-outline"
                        >
                            Exit Practice
                        </button>
                    </div>

                    <div className="practice-area bg-white p-4 rounded-lg mb-4 leading-relaxed">
                        {renderPracticeVerse()}
                    </div>

                    <div className="flex flex-wrap gap-3 mb-4">
                        <button
                            onClick={increaseHiddenWords}
                            className="btn btn-sm bg-indigo-600 text-white"
                        >
                            <EyeOff className="h-4 w-4 mr-1" />
                            Hide More Words
                        </button>

                        <button
                            onClick={() => setHiddenWords([])}
                            className="btn btn-sm bg-indigo-200 text-indigo-800"
                        >
                            <Eye className="h-4 w-4 mr-1" />
                            Show All Words
                        </button>
                    </div>

                    <div className="flex justify-between items-center bg-white p-3 rounded-lg">
                        <p className="text-sm text-gray-600">
                            Complete when you can recite the entire verse from memory
                        </p>
                        <button
                            onClick={() => markAsReviewed(activeVerse.id)}
                            className="btn btn-sm btn-success"
                        >
                            <Check className="h-4 w-4 mr-1" />
                            Mark as Reviewed
                        </button>
                    </div>

                    <div className="mt-6 text-center">
                        <details className="inline-block">
                            <summary className="cursor-pointer text-indigo-600 hover:text-indigo-800">
                                Show full verse
                            </summary>
                            <p className="mt-2 p-3 bg-white rounded text-left">
                                {activeVerse.text}
                            </p>
                        </details>
                    </div>
                </div>
            ) : (
                <>
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Scripture Memorization</h3>
                            <button
                                onClick={() => setShowAddForm(!showAddForm)}
                                className="btn btn-primary btn-sm"
                            >
                                {showAddForm ? 'Cancel' : 'Add Verse'}
                            </button>
                        </div>

                        {showAddForm && (
                            <form onSubmit={handleAddVerse} className="mb-6 bg-blue-50 p-4 rounded-lg">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Scripture Reference
                                        </label>
                                        <input
                                            type="text"
                                            value={newReference}
                                            onChange={(e) => setNewReference(e.target.value)}
                                            placeholder="e.g., John 3:16"
                                            className="form-input w-full"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Verse Text
                                        </label>
                                        <textarea
                                            value={newText}
                                            onChange={(e) => setNewText(e.target.value)}
                                            placeholder="Enter the verse text here..."
                                            className="form-textarea w-full"
                                            rows={2}
                                        />
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    className="btn btn-sm btn-primary"
                                    disabled={!newReference.trim() || !newText.trim()}
                                >
                                    Add to Memory Verses
                                </button>
                            </form>
                        )}

                        <div className="bg-orange-100 p-3 rounded-lg mb-4">
                            <h4 className="font-medium text-orange-800 mb-1 flex items-center">
                                <Clock className="h-4 w-4 mr-1" />
                                Verses Due for Review
                            </h4>

                            {getVersesForReview().length === 0 ? (
                                <p className="text-sm text-orange-700">No verses need review right now. Great job!</p>
                            ) : (
                                <div className="space-y-2">
                                    {getVersesForReview().map(verse => (
                                        <div
                                            key={verse.id}
                                            className="flex justify-between items-center bg-white p-2 rounded"
                                        >
                                            <span className="font-medium">{verse.reference}</span>
                                            <button
                                                onClick={() => startPractice(verse)}
                                                className="btn btn-xs bg-orange-500 text-white"
                                            >
                                                Review Now
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="space-y-3">
                        {memoryVerses.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg">
                                <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 mb-3">No memory verses added yet</p>
                                <button
                                    onClick={() => setShowAddForm(true)}
                                    className="btn btn-primary btn-sm"
                                >
                                    Add Your First Verse
                                </button>
                            </div>
                        ) : (
                            memoryVerses.map(verse => (
                                <div
                                    key={verse.id}
                                    className={`p-4 border rounded-lg ${verse.isFavorite ? 'bg-yellow-50 border-yellow-200' : 'bg-white'
                                        }`}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <h4 className="font-medium flex items-center">
                                                {verse.isFavorite && <Star className="h-4 w-4 text-yellow-500 mr-1" />}
                                                {verse.reference}
                                            </h4>
                                            <p className="text-sm text-gray-600">
                                                Added: {formatDate(verse.dateAdded)}
                                            </p>
                                        </div>

                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => toggleFavorite(verse.id)}
                                                className="btn btn-xs btn-icon"
                                                title={verse.isFavorite ? "Remove from favorites" : "Add to favorites"}
                                            >
                                                {verse.isFavorite ? (
                                                    <StarOff className="h-4 w-4 text-gray-400" />
                                                ) : (
                                                    <Star className="h-4 w-4 text-gray-400" />
                                                )}
                                            </button>

                                            <button
                                                onClick={() => handleRemoveVerse(verse.id)}
                                                className="btn btn-xs btn-icon text-red-500"
                                                title="Remove verse"
                                            >
                                                &times;
                                            </button>
                                        </div>
                                    </div>

                                    <p className="mb-3">{verse.text}</p>

                                    <div className="flex justify-between items-center mt-2">
                                        <span className="text-xs text-indigo-600">
                                            {getNextReviewDate(verse.id)}
                                        </span>

                                        <button
                                            onClick={() => startPractice(verse)}
                                            className="btn btn-xs bg-indigo-100 text-indigo-700 flex items-center"
                                        >
                                            <Repeat className="h-3 w-3 mr-1" />
                                            Practice
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ScriptureMemorization;
